<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const fieldTable = [
  { field: 'Nama Promo', desc: 'Nama untuk memudahkan Anda mengenali promo ini, contoh: "Diskon Jam Ngantuk".', wajib: '✅ Ya' },
  { field: 'Tipe Diskon', desc: 'Persentase (%) dari total, atau Nominal (Rp) tetap.', wajib: '✅ Ya' },
  { field: 'Nilai', desc: 'Besaran diskonnya — mis. 10 (untuk 10%) atau 10000 (untuk Rp10.000).', wajib: '✅ Ya' },
  { field: 'Berlaku Untuk (Scope)', desc: 'Seluruh Order, Produk Tertentu, atau Kategori Tertentu.', wajib: '✅ Ya' },
  { field: 'Min. Pembelian', desc: 'Promo baru aktif kalau subtotal belanja sudah mencapai nominal ini.', wajib: 'Opsional' },
  { field: 'Maks. Potongan', desc: 'Batas atas nominal potongan — lihat penjelasan khusus di bawah.', wajib: 'Opsional' },
  { field: 'Mulai / Berakhir', desc: 'Rentang tanggal promo berlaku. Kosongkan kalau mau berlaku terus.', wajib: 'Opsional' },
  { field: 'Jam Mulai / Selesai', desc: 'Promo hanya aktif di jam tertentu, mis. 14:00–17:00 untuk "jam ngantuk".', wajib: 'Opsional' },
  { field: 'Hari Aktif', desc: 'Pilih hari tertentu (mis. cuma Senin–Jumat). Kosongkan = berlaku semua hari.', wajib: 'Opsional' },
  { field: 'Terapkan Otomatis', desc: 'Lihat bagian "Auto-Apply vs Manual" di bawah.', wajib: '—' },
]

const faq = [
  {
    q: 'Kalau ada 2 promo auto-apply yang sama-sama memenuhi syarat, apakah keduanya diterapkan sekaligus?',
    a: 'Bisa. Kalau transaksi memenuhi syarat lebih dari satu promo auto-apply, semua promo tersebut diterapkan bersamaan — masing-masing muncul sebagai baris terpisah di ringkasan belanja dan struk, bukan digabung jadi satu angka.',
  },
  {
    q: 'Saya buat promo untuk "Produk Tertentu" tapi kasir tidak bisa menyalakan/mematikannya manual saat checkout — kenapa?',
    a: 'Ini memang sesuai desain. Promo dengan scope Produk Tertentu atau Kategori Tertentu HANYA bisa berjalan lewat Auto-Apply (otomatis menyala sendiri kalau syaratnya terpenuhi) — tidak bisa dipilih manual oleh kasir saat checkout. Kalau Anda ingin promo yang bisa dinyala-matikan manual oleh kasir per transaksi, buat dengan scope "Seluruh Order".',
  },
  {
    q: 'Kenapa promo yang sudah saya hapus masih muncul di daftar (statusnya Nonaktif)?',
    a: 'Kalau promo itu sudah pernah dipakai di sebuah transaksi, sistem tidak benar-benar menghapus datanya (supaya riwayat transaksi lama tetap lengkap dan bisa ditelusuri) — promo hanya dinonaktifkan otomatis. Promo yang belum pernah dipakai sama sekali akan benar-benar terhapus.',
  },
  {
    q: 'Nilai diskon persentase saya isi lebih dari 100, kenapa ditolak?',
    a: 'Nilai untuk tipe Persentase dibatasi maksimal 100 (yaitu 100%, gratis total). Kalau maksud Anda potongan besar dalam Rupiah, gunakan tipe Nominal (Rp), bukan Persentase.',
  },
  {
    q: 'Tombol "Tambah Promo" tidak muncul di layar saya, kenapa?',
    a: 'Tombol ini hanya muncul untuk akun yang punya izin membuat Promo (biasanya role Owner dan Manager). Kalau Anda merasa harus punya akses ini, minta Owner cek pengaturan izin akun Anda di menu User & Role.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink
            to="/userguide"
            class="text-decoration-none text-medium-emphasis text-body-2"
          >
            User Guide
          </RouterLink>
          <VIcon
            icon="ri-arrow-right-s-line"
            size="16"
            class="text-medium-emphasis"
          />
          <span class="text-body-2">Promo &amp; Diskon</span>
        </div>
        <RouterLink to="/userguide">
          <VBtn
            variant="text"
            prepend-icon="ri-arrow-left-line"
            size="small"
            class="mb-4"
          >
            Kembali ke Daftar Modul
          </VBtn>
        </RouterLink>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar
            color="primary"
            variant="tonal"
            size="64"
            rounded="lg"
            class="me-4"
          >
            <VIcon
              icon="ri-price-tag-3-line"
              size="36"
            />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-1">
              Promo &amp; Diskon
            </h4>
            <p class="text-body-1 text-medium-emphasis mb-0">
              Buat diskon otomatis atau manual untuk seluruh order, produk, atau kategori tertentu
            </p>
          </div>
        </div>
        <VAlert
          type="info"
          variant="tonal"
        >
          <strong>Tujuan halaman ini:</strong>
          Memandu Anda membuat dan mengatur Promo/Diskon — kapan sebaiknya pakai persen atau nominal,
          kenapa "Maks. Potongan" penting, dan bedanya promo otomatis vs manual saat kasir checkout.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Konsep -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-book-open-line"
              class="me-2"
              color="primary"
            />
            Apa itu Promo/Diskon?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              <strong>Promo/Diskon</strong> adalah potongan harga yang Anda atur sendiri sebagai
              pemilik toko — bisa berlaku untuk semua transaksi (mis. "Diskon 10% Semua Order"),
              produk tertentu (mis. "Diskon Americano 20%"), atau kategori tertentu
              (mis. "Diskon Semua Kue 15%").
            </p>
            <p class="text-body-2 mb-0">
              Ada dua cara menghitung nilainya:
            </p>
            <VRow class="mt-2">
              <VCol
                cols="12"
                sm="6"
              >
                <VCard
                  variant="tonal"
                  color="info"
                  class="h-100"
                >
                  <VCardText class="pa-4">
                    <div class="d-flex align-center gap-2 mb-2">
                      <VIcon
                        icon="ri-percent-line"
                        size="22"
                      />
                      <strong class="text-body-1">Persentase (%)</strong>
                    </div>
                    <p class="text-body-2 mb-0">
                      Potongan mengikuti besar transaksi. Diskon 10% dari belanja Rp50.000
                      = potongan Rp5.000; dari belanja Rp2.000.000 = potongan Rp200.000.
                      Cocok untuk promo umum, tapi <strong>berisiko</strong> pada transaksi besar
                      — lihat penjelasan "Maks. Potongan" di bawah.
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
              <VCol
                cols="12"
                sm="6"
              >
                <VCard
                  variant="tonal"
                  color="success"
                  class="h-100"
                >
                  <VCardText class="pa-4">
                    <div class="d-flex align-center gap-2 mb-2">
                      <VIcon
                        icon="ri-money-dollar-circle-line"
                        size="22"
                      />
                      <strong class="text-body-1">Nominal (Rp)</strong>
                    </div>
                    <p class="text-body-2 mb-0">
                      Potongan tetap berapa pun besar transaksinya. Diskon Rp5.000 tetap
                      Rp5.000, baik belanja Rp20.000 maupun Rp2.000.000. Lebih mudah
                      diprediksi biayanya, cocok untuk promo produk spesifik.
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara Membuat -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-play-circle-line"
              class="me-2"
              color="success"
            />
            Cara Membuat Promo Baru
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4 mb-0">
              <li class="mb-3">
                Buka menu <strong>Marketing → Promo &amp; Diskon</strong> dari sidebar kiri.
              </li>
              <li class="mb-3">
                Klik tombol <strong>"Tambah Promo"</strong> di pojok kanan atas.
              </li>
              <li class="mb-3">
                Isi <strong>Nama Promo</strong> dan (opsional) deskripsi singkat.
              </li>
              <li class="mb-3">
                Pilih <strong>Tipe Diskon</strong> (Persentase atau Nominal) lalu isi <strong>Nilai</strong>-nya.
              </li>
              <li class="mb-3">
                Pilih <strong>Berlaku Untuk</strong>: Seluruh Order, Produk Tertentu, atau Kategori
                Tertentu. Kalau pilih Produk/Kategori Tertentu, pilih juga produk/kategori mana
                saja di kolom yang muncul.
              </li>
              <li class="mb-3">
                (Opsional) Isi <strong>Min. Pembelian</strong> dan <strong>Maks. Potongan</strong> kalau
                perlu — lihat penjelasan detail di bagian bawah halaman ini.
              </li>
              <li class="mb-3">
                (Opsional) Atur <strong>periode tanggal</strong>, <strong>jam aktif</strong>, dan
                <strong>hari aktif</strong> kalau promo ini hanya untuk waktu tertentu saja.
              </li>
              <li class="mb-3">
                Tentukan apakah promo <strong>diterapkan otomatis</strong> atau harus dinyalakan
                manual oleh kasir — lihat penjelasan "Auto-Apply vs Manual" di bawah.
              </li>
              <li class="mb-0">
                Pastikan toggle <strong>Aktif</strong> menyala, lalu klik <strong>"Simpan"</strong>.
              </li>
            </ol>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Tabel field -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-list-check-2"
              class="me-2"
              color="info"
            />
            Penjelasan Tiap Kolom
          </VCardTitle>
          <VCardText>
            <VTable>
              <thead>
                <tr>
                  <th>Kolom</th>
                  <th>Penjelasan</th>
                  <th>Wajib?</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in fieldTable"
                  :key="row.field"
                >
                  <td><strong>{{ row.field }}</strong></td>
                  <td class="text-body-2 py-2">
                    {{ row.desc }}
                  </td>
                  <td class="text-body-2">
                    {{ row.wajib }}
                  </td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Maks Potongan -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard
          color="warning"
          variant="tonal"
        >
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-shield-check-line"
              class="me-2"
            />
            🛡️ Kenapa "Maks. Potongan" Penting?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Anggap <strong>Maks. Potongan</strong> seperti <strong>pengaman/rem tangan</strong> untuk
              promo tipe Persentase — supaya toko Anda tidak rugi besar kalau kebetulan ada
              transaksi dengan nilai sangat besar.
            </p>
            <VCard
              variant="outlined"
              class="mb-3"
            >
              <VCardText>
                <p class="text-body-2 mb-2">
                  <strong>Contoh tanpa Maks. Potongan:</strong>
                </p>
                <p class="text-body-2 mb-2">
                  Anda buat promo <strong>"Diskon 20%"</strong> untuk seluruh order, tanpa mengisi
                  Maks. Potongan. Suatu hari ada pelanggan pesan catering besar senilai
                  <strong>Rp5.000.000</strong> — promo ini otomatis memotong
                  <strong class="text-error">Rp1.000.000</strong>. Padahal niat awal Anda cuma
                  ingin memberi diskon kecil untuk transaksi sehari-hari.
                </p>
                <p class="text-body-2 mb-0">
                  <strong>Dengan Maks. Potongan Rp50.000:</strong>
                  Potongan untuk transaksi Rp5.000.000 tadi otomatis dibatasi jadi
                  <strong class="text-success">Rp50.000 saja</strong> — walau 20% dari 5 juta
                  jauh lebih besar dari itu. Transaksi kecil sehari-hari tetap dapat diskon
                  penuh 20% seperti biasa (selama potongannya belum menyentuh batas Rp50.000).
                </p>
              </VCardText>
            </VCard>
            <p class="text-body-2 mb-0">
              <strong>Aturan sederhana:</strong> kalau Anda pakai tipe <strong>Persentase</strong>
              untuk promo yang berlaku ke Seluruh Order, sangat disarankan selalu isi Maks. Potongan.
              Untuk tipe <strong>Nominal (Rp)</strong>, kolom ini biasanya tidak perlu diisi karena
              potongannya memang sudah tetap.
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Auto-apply vs manual -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-toggle-line"
              class="me-2"
              color="primary"
            />
            Auto-Apply vs Manual — Apa Bedanya?
          </VCardTitle>
          <VCardText>
            <VRow>
              <VCol
                cols="12"
                sm="6"
              >
                <VCard
                  variant="tonal"
                  color="primary"
                  class="h-100"
                >
                  <VCardText class="pa-4">
                    <VChip
                      color="primary"
                      size="small"
                      class="mb-2"
                    >
                      Otomatis
                    </VChip>
                    <p class="text-body-2 mb-0">
                      Promo langsung aktif sendiri di layar kasir begitu syaratnya terpenuhi
                      (tanggal, jam, hari, dan Min. Pembelian cocok) — <strong>kasir tidak perlu
                        melakukan apa-apa</strong>. Muncul sebagai chip hijau bertanda centang
                      di bagian "Promo Aktif" pada layar POS.
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
              <VCol
                cols="12"
                sm="6"
              >
                <VCard
                  variant="tonal"
                  class="h-100"
                >
                  <VCardText class="pa-4">
                    <VChip
                      size="small"
                      class="mb-2"
                    >
                      Manual
                    </VChip>
                    <p class="text-body-2 mb-0">
                      Promo <strong>muncul sebagai pilihan</strong> di layar kasir, tapi kasir harus
                      klik chip promo tersebut secara manual untuk mengaktifkannya di transaksi
                      itu. Cocok untuk promo yang hanya berlaku dengan syarat tertentu yang tidak
                      bisa dicek sistem otomatis (mis. "khusus member komunitas").
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>
            <VAlert
              type="warning"
              variant="tonal"
              class="mt-4 mb-0"
            >
              <strong>Catatan penting:</strong> promo dengan Berlaku Untuk = <strong>Produk Tertentu</strong>
              atau <strong>Kategori Tertentu</strong> hanya bisa jalan lewat <strong>Otomatis</strong> —
              kasir tidak bisa menyalakannya secara manual saat checkout. Pilihan manual hanya
              tersedia untuk promo dengan Berlaku Untuk = <strong>Seluruh Order</strong>.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Tips -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard
          color="success"
          variant="tonal"
        >
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-lightbulb-line"
              class="me-2"
            />
            💡 Tips Praktis
          </VCardTitle>
          <VCardText>
            <VList
              density="compact"
              bg-color="transparent"
            >
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Selalu isi Maks. Potongan untuk promo Persentase Seluruh Order</strong> —
                  ini langkah paling sering terlewat dan paling berisiko kalau lupa.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Manfaatkan Jam &amp; Hari Aktif</strong> untuk mendorong penjualan di jam
                  sepi, mis. diskon khusus pukul 14:00–17:00 di hari kerja.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Nonaktifkan dulu, jangan langsung hapus</strong> promo yang sedang tidak
                  dipakai tapi mungkin dipakai lagi nanti (mis. promo musiman) — matikan toggle
                  Aktif, bukan dihapus, supaya bisa dinyalakan lagi tahun depan tanpa isi ulang.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mt-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          ❓ Pertanyaan yang Sering Ditanyakan
        </h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel
            v-for="(item, i) in faq"
            :key="i"
          >
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">
                {{ item.a }}
              </p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Related -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          🔗 Halaman Terkait
        </h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'Voucher & Kupon', sub: 'Diskon lewat kode, bukan otomatis', icon: 'ri-coupon-3-line', color: 'primary', to: '/userguide/promo-voucher-loyalty/voucher' },
              { title: 'Program Loyalty', sub: 'Poin & stempel untuk pelanggan setia', icon: 'ri-vip-diamond-line', color: 'secondary', to: '/userguide/promo-voucher-loyalty/loyalty' },
              { title: 'Gabungan di 1 Transaksi', sub: 'Promo + Voucher + Poin bareng-bareng', icon: 'ri-git-merge-line', color: 'success', to: '/userguide/promo-voucher-loyalty/kombinasi' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink
              :to="link.to"
              class="text-decoration-none"
            >
              <VCard
                variant="outlined"
                hover
              >
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar
                    :color="link.color"
                    variant="tonal"
                    size="40"
                    rounded
                  >
                    <VIcon
                      :icon="link.icon"
                      size="22"
                    />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">
                      {{ link.title }}
                    </p>
                    <p class="text-caption text-medium-emphasis mb-0">
                      {{ link.sub }}
                    </p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>